import React, { useEffect, useState } from 'react';
import { AnimatePresence, type Variants, motion } from 'framer-motion';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { useTranslation } from 'react-i18next';
import useImagePreloader from '../../../hooks/utility/useImagePreloader';

type Custom = { direction: 1 | -1; isClosing: boolean };

const pageVariants: Variants = {
  enter: ({ direction }: Custom) => ({
    x: direction > 0 ? '100vw' : '-100vw',
    opacity: 1,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: ({ direction, isClosing }: Custom) => {
    if (isClosing) {
      return {
        x: 0,
        opacity: 0,
        transition: { opacity: { duration: 0.4 }, x: { duration: 0 } },
      };
    }

    return {
      x: direction > 0 ? '-100vw' : '100vw',
      opacity: 0,
    };
  },
};

const pageTransition = {
  x: { type: 'tween', ease: 'easeInOut', duration: 0.45 },
  opacity: { duration: 0.25 },
};

const IS_INTRO_SCREENS_SHOWN_KEY = 'mobile_app_is_intro_screens_shown';

const IntroScreen = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Components:IntroScreen' });
  const { data } = useMobileApp();
  const { preloadImages } = useImagePreloader();

  const steps = data?.app?.intro_screen_images;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const [isClosing, setIsClosing] = useState(false);
  const [isIntroScreensShown, setIsIntroScreensShown] = useState(() =>
    Boolean(localStorage.getItem(IS_INTRO_SCREENS_SHOWN_KEY)),
  );

  useEffect(() => {
    if (steps && steps.length > 0) {
      preloadImages(steps);
    }
  }, [steps]);

  if (!steps?.length) {
    return null;
  }

  if (isIntroScreensShown) {
    return null;
  }

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const next = (): void => {
    if (isLast) {
      return skip();
    }

    setDirection(1);
    setIndex((i) => i + 1);
  };

  const skip = (): void => {
    setIsClosing(true);
  };

  return (
    <div className="fixed inset-0 z-[100000] overflow-hidden bg-black">
      <AnimatePresence
        initial={false}
        custom={{ direction, isClosing }}
        onExitComplete={() => {
          if (isClosing) {
            localStorage.setItem(IS_INTRO_SCREENS_SHOWN_KEY, 'true');
            setIsIntroScreensShown(true);
          }
        }}
      >
        {!isClosing && (
          <motion.div
            key={step}
            custom={{ direction, isClosing }}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
            className="absolute inset-0 transform-gpu"
            style={{
              backgroundImage: `url(${step})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              willChange: 'transform, opacity',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/25 to-black/70" />
            <div className="absolute inset-x-0 bottom-xl flex justify-center">
              <div className="w-full max-w-sm px-6 flex flex-col items-center text-center text-white">
                <h2 className="text-3xl font-semibold tracking-tight">{t(`steps.${index}.title`)}</h2>
                <p className="mt-3 text-sm text-white/80 leading-relaxed">{t(`steps.${index}.description`)}</p>
                <button
                  onClick={next}
                  className="mt-8 size-20 rounded-full bg-brand-primary flex items-center justify-center"
                >
                  <span className="text-xl">→</span>
                </button>
                <button className="mt-4 text-sm text-white/70" onClick={skip}>
                  {t('buttons.skip')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntroScreen;
